import React, { type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import OfficeBuildingFilledIcon from '@atlaskit/icon/glyph/office-building-filled';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey } from '@atlassian/jira-project-context-service';
import { useRouterActions } from '@atlassian/jira-router';
import type { OrganizationOption } from '../../../types';
import { getProfileUrl } from '../../details';

export const OrganizationField = ({ value }: { value: OrganizationOption }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { push } = useRouterActions();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const profileURL = getProfileUrl(projectKey, 'ORGANIZATION', value.id);

	const onClick = (e: SyntheticEvent) => {
		e.preventDefault();
		push(profileURL);
		fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'viewCustomerContextProfile', {
			context: 'ORGANIZATION',
		});
	};

	return (
		<FieldContainer data-testid="servicedesk-customer-service-issue-view-panel.common.ui.organization.organization-field.field-container">
			<IconSquare isNewUI={ff('products-and-entitlements-details-m2_45b1j')}>
				<OfficeBuildingFilledIcon
					primaryColor={token('color.icon', '#44546F')}
					size="small"
					label=""
				/>
			</IconSquare>
			<NameWrapper>
				{ff('products-and-entitlements-details-m2_45b1j') ? (
					<a onClick={onClick} href={profileURL}>
						{value.name}
					</a>
				) : (
					<span>{value.name}</span>
				)}
			</NameWrapper>
		</FieldContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldContainer = styled.div({
	height: `${token('space.400', '32px')}`,
	alignItems: 'center',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NameWrapper = styled.span({
	display: 'flex',
	flexWrap: 'wrap',
	lineHeight: 1,
	wordBreak: 'break-word',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconSquare = styled.div<{ isNewUI?: boolean }>({
	height: `${token('space.200', '16px')}`,
	width: `${token('space.200', '16px')}`,
	display: 'flex',
	borderRadius: '4px',
	padding: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: (props) =>
		props.isNewUI ? `${token('space.100', '8px')}` : `${token('space.150', '12px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: `${token('color.background.neutral', colors.N30)}`,
});
